import { useMediaQuery, useTheme } from '@mui/material';
import Map from '@vis.gl/react-maplibre';
import { Fragment, useState } from 'react';

import { ContextMenuMarker } from '@/components/map/ContextMenuMarker';
import { LayerControl } from '@/components/map/LayerControl';
import { LevelOfServiceLegend } from '@/components/map/LevelOfServiceLegend';
import { PARK_AND_RIDE_LAYER, ParkAndRideLayer } from '@/components/map/ParkAndRideLayer';
import { RouteLayers } from '@/components/map/RouteLayers';
import { RouteLocationMarkers } from '@/components/map/RouteLocationMarkers';
import { TRAFFIC_INFO_BOARD_LAYER } from '@/components/map/TrafficInfoBoardLayer';
import { TrafficManagementLayers } from '@/components/map/TrafficManagementLayers';
import { TrafficReportLayers } from '@/components/map/TrafficReportLayers';
import { ParkAndRidePopup } from '@/components/popup/ParkAndRidePopup';
import { StrategyPopup } from '@/components/popup/StrategyPopup';
import { TrafficInfoBoardPopup } from '@/components/popup/TrafficInfoBoardPopup';
import { TrafficReportPopup } from '@/components/popup/TrafficReportPopup';
import { useMapStore } from '@/stores/useMapStore';
import type { ParkAndRideFeature } from '@/types/ParkAndRideFeature';
import type { StrategyFeature } from '@/types/StrategyFeature';
import { StrategyLayerType } from '@/types/StrategyLayerType';
import type { TrafficInfoBoardFeature } from '@/types/TrafficInfoBoardFeature';
import type { TrafficReportFeature } from '@/types/TrafficReportFeature';
import { TrafficReportLayerType } from '@/types/TrafficReportLayerType';

export function RoutingMap() {
  const viewState = useMapStore((state) => state.viewState);
  const { setViewState } = useMapStore((state) => state.actions);

  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));

  const [contextMenu, setContextMenu] = useState<[number, number]>();

  const [trafficReportPopupFeature, setTrafficReportPopupFeature] = useState<TrafficReportFeature>();
  const [strategyPopupFeature, setStrategyPopupFeature] = useState<StrategyFeature>();
  const [trafficInfoBoardPopupFeature, setTrafficInfoBoardPopupFeature] = useState<TrafficInfoBoardFeature>();
  const [parkAndRideFeature, setParkAndRideFeature] = useState<ParkAndRideFeature>();

  const { trafficReport, trafficManagement, parking } = useMapStore((state) => state.layersControl);

  return (
    <Map
      attributionControl={false}
      {...viewState}
      dragRotate={false}
      mapStyle="/api/map-style/basemap-de-web-top.json"
      onMove={(event) => setViewState(event.viewState)}
      pitchWithRotate={false}
      touchPitch={false}
      interactiveLayerIds={[
        ...(trafficReport ? [TrafficReportLayerType.TRAFFIC_REPORT] : []),
        ...(trafficManagement ? [TRAFFIC_INFO_BOARD_LAYER, ...Object.values(StrategyLayerType)] : []),
        ...(parking ? [PARK_AND_RIDE_LAYER] : []),
      ]}
      onClick={(event) => {
        const [feature] = event.features ?? [];
        if (!feature?.layer) return;

        if ((TrafficReportLayerType.TRAFFIC_REPORT as string) === feature.layer.id) {
          setTrafficReportPopupFeature(feature as unknown as TrafficReportFeature);
        } else if (TRAFFIC_INFO_BOARD_LAYER === feature.layer.id) {
          setTrafficInfoBoardPopupFeature(feature as unknown as TrafficInfoBoardFeature);
        } else if (Object.values<string>(StrategyLayerType).includes(feature.layer.id)) {
          setStrategyPopupFeature(feature as unknown as StrategyFeature);
        } else if (PARK_AND_RIDE_LAYER === feature.layer.id) {
          setParkAndRideFeature(feature as unknown as ParkAndRideFeature);
        }
      }}
      onContextMenu={(event) => {
        setContextMenu([event.lngLat.lng, event.lngLat.lat]);
      }}
    >
      {!(
        mobile &&
        (trafficReportPopupFeature ?? strategyPopupFeature ?? trafficInfoBoardPopupFeature ?? parkAndRideFeature)
      ) && (
        <Fragment>
          <LayerControl />
          <LevelOfServiceLegend />
        </Fragment>
      )}

      <TrafficReportLayers />
      <TrafficManagementLayers />

      <RouteLayers />
      <RouteLocationMarkers />

      {parking ? <ParkAndRideLayer /> : null}

      <TrafficReportPopup feature={trafficReportPopupFeature} onClose={() => setTrafficReportPopupFeature(undefined)} />
      <StrategyPopup feature={strategyPopupFeature} onClose={() => setStrategyPopupFeature(undefined)} />
      <TrafficInfoBoardPopup
        feature={trafficInfoBoardPopupFeature}
        onClose={() => setTrafficInfoBoardPopupFeature(undefined)}
      />
      <ParkAndRidePopup feature={parkAndRideFeature} onClose={() => setParkAndRideFeature(undefined)} />

      <ContextMenuMarker
        latitude={contextMenu?.[1]}
        longitude={contextMenu?.[0]}
        onClose={() => setContextMenu(undefined)}
      />
    </Map>
  );
}
